import { cloneDeep } from 'lodash'

import { AskResponse, Citation } from '../../api'
import { t } from 'i18next'

export type ParsedAnswer = {
  citations: Citation[]
  markdownFormatText: string,
  generated_chart: string | null
} | null

export const enumerateCitations = (citations: Citation[]) => {
  const filepathMap = new Map()
  for (const citation of citations) {
    const { filepath } = citation
    let part_i = 1
    if (filepathMap.has(filepath)) {
      part_i = filepathMap.get(filepath) + 1
    }
    filepathMap.set(filepath, part_i)
    citation.part_index = part_i
  }
  return citations
}

/**
 * Helper function to detect and normalize the Azure RAG "no data found" message
 * Handles the specific message returned when Azure RAG cannot find relevant information
 */
function detectAzureNoDataMessage(text: string): boolean {
  // Normalize whitespace and case for robust detection
  const normalizedText = text.trim().toLowerCase().replace(/\s+/g, ' ')

  const azureNoDataMessages = [
    "the requested information is not available in the retrieved data. please try another query or topic.",
    "the requested information is not found in the retrieved data. please try another query or topic."
  ]

  return azureNoDataMessages.includes(normalizedText)
}

export function parseAnswer(answer: AskResponse): ParsedAnswer {
  if (typeof answer.answer !== "string") return null
  let answerText = answer.answer

  // Check for Azure RAG "no data found" message and replace it
  // This must happen before any other processing (citations, markdown, etc.)
  if (detectAzureNoDataMessage(answerText)) {
    answerText = t('noDataFound')
  }
  const citationLinks = answerText.match(/\[(doc\d\d?\d?)]/g)

  if (!citationLinks) {
    return {
      citations: [],
      markdownFormatText: answerText,
      generated_chart: answer.generated_chart
    }
  }

  const lengthDocN = '[doc'.length

  // Step 1: Extract all citations referenced in the text
  const referencedCitations = [] as Citation[]
  const citationMap = new Map<string, number>() // original index -> position in referencedCitations

  citationLinks.forEach(link => {
    const citationIndex = link.slice(lengthDocN, link.length - 1)
    const citation = answer.citations[Number(citationIndex) - 1]

    if (citation && !citationMap.has(citationIndex)) {
      const clonedCitation = cloneDeep(citation)
      clonedCitation.id = citationIndex // original doc index
      referencedCitations.push(clonedCitation)
      citationMap.set(citationIndex, referencedCitations.length - 1)
    }
  })

  // Step 2: Deduplicate citations by URL/filepath before reindexing
  const deduplicatedCitations = deduplicateCitations(referencedCitations)

  // Step 3: Create mapping from original citation to final deduplicated citation
  const originalToFinalMap = new Map<string, number>()

  referencedCitations.forEach((originalCitation, originalIndex) => {
    const finalIndex = deduplicatedCitations.findIndex(finalCitation =>
      areCitationsEqual(originalCitation, finalCitation)
    )
    if (finalIndex !== -1) {
      originalToFinalMap.set(originalCitation.id, finalIndex)
    }
  })

  // Step 4: Update text references using the mapping
  // Process each unique citation link type once
  const processedLinks = new Set<string>()

  citationLinks.forEach(link => {
    if (processedLinks.has(link)) return

    const citationIndex = link.slice(lengthDocN, link.length - 1)
    const finalIndex = originalToFinalMap.get(citationIndex)

    if (finalIndex !== undefined) {
      const displayIndex = finalIndex + 1 // 1-based display
      // Replace ALL instances of this link with the correct display index
      answerText = answerText.replaceAll(link, ` ^${displayIndex}^ `)
      processedLinks.add(link)
    }
  })

  // Step 5: Set reindex_id for final citations
  deduplicatedCitations.forEach((citation, index) => {
    citation.reindex_id = (index + 1).toString()
  })

  const finalCitations = enumerateCitations(deduplicatedCitations)

  return {
    citations: finalCitations,
    markdownFormatText: answerText,
    generated_chart: answer.generated_chart
  }
}

// Helper function to deduplicate citations by URL/filepath
function deduplicateCitations(citations: Citation[]): Citation[] {
  const urlMap = new Map<string, Citation>()

  citations.forEach(citation => {
    const key = citation.url || citation.filepath || citation.id
    if (key && !urlMap.has(key)) {
      urlMap.set(key, citation)
    }
  })

  return Array.from(urlMap.values())
}

// Helper function to check if two citations are equal for deduplication
function areCitationsEqual(citation1: Citation, citation2: Citation): boolean {
  const key1 = citation1.url || citation1.filepath || citation1.id
  const key2 = citation2.url || citation2.filepath || citation2.id
  return key1 === key2
}
